import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Check, Wrench, Package, Truck } from "lucide-react";

const STEPS = [
  ["received", "Received"], ["in_diagnosis", "In Diagnosis"], ["awaiting_parts", "Waiting for Parts"], ["testing", "Testing"],
  ["qa_review", "Quality Check"], ["ready_for_dispatch", "Ready for Dispatch"], ["dispatched", "Dispatched"],
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const titleCase = (s = "") => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#EEF2F7] px-4 py-10 text-slate-900" data-testid="status-page">
      <div className="mx-auto max-w-2xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5"><img src="/logo-amax.png" alt="amax" className="h-8" /></div>
        <div className="bg-[#0B0E14] px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">Repair Status <span className="text-blue-500">●</span></div>
        <div className="p-6">{children}</div>
      </div>
      <p className="mt-4 text-center text-xs text-slate-400">AMAX RepairOps · this page updates as your repair progresses</p>
    </div>
  );
}

function Info({ rows }) {
  return (
    <dl className="mb-6 grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border border-slate-200 p-4 text-sm sm:grid-cols-3">
      {rows.filter(([, v]) => v).map(([k, v]) => (
        <div key={k}><dt className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{k}</dt><dd className="font-semibold">{v}</dd></div>
      ))}
    </dl>
  );
}

function Timeline({ status, history }) {
  const idx = STEPS.findIndex(([v]) => v === status);
  const at = Object.fromEntries((history || []).map((h) => [h.status, h.at]));
  return (
    <ol className="mb-6 space-y-0" data-testid="status-timeline">
      {STEPS.map(([v, label], i) => {
        const done = i < idx, cur = i === idx;
        if (v === "awaiting_parts" && !at[v] && !cur) return null;
        return (
          <li key={v} className="flex gap-3" data-testid={`step-${v}`}>
            <div className="flex flex-col items-center">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full border-2 ${done ? "border-blue-600 bg-blue-600 text-white" : cur ? "border-blue-600 bg-white text-blue-600" : "border-slate-300 bg-white text-slate-300"}`}>
                {done ? <Check size={14} /> : <span className={`h-2 w-2 rounded-full ${cur ? "animate-pulse bg-blue-600" : "bg-slate-300"}`} />}
              </div>
              {i < STEPS.length - 1 && <div className={`w-0.5 flex-1 ${done ? "bg-blue-600" : "bg-slate-200"}`} style={{ minHeight: 18 }} />}
            </div>
            <div className="pb-4">
              <div className={`text-sm font-semibold ${cur ? "text-blue-700" : done ? "text-slate-900" : "text-slate-400"}`}>{label}{cur && " · current"}</div>
              {at[v] && <div className="font-mono text-[11px] text-slate-500">{fmt(at[v])}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function JobView({ job }) {
  return (
    <>
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight" data-testid="status-heading">{job.status_label}</h1>
      <p className="mb-5 text-sm text-slate-500">Job {job.job_number}</p>
      <Info rows={[["Equipment", job.equipment_name], ["Model", job.model_number], ["Brand", job.brand], ["Serial", job.serial_number],
        ["Service", titleCase(job.repair_type)], ["Received", fmt(job.received_at)]]} />
      <Timeline status={job.status} history={job.history} />
      {job.fault && (job.fault.reported || job.fault.found) && (
        <div className="mb-4 rounded-lg border border-slate-200 p-4 text-sm" data-testid="status-fault">
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Wrench size={12} /> Fault summary</div>
          {job.fault.reported && <p><b>Reported:</b> {job.fault.reported}</p>}
          {job.fault.found && <p><b>Found:</b> {job.fault.found}</p>}
          {job.fault.action && <p><b>Action:</b> {job.fault.action}</p>}
        </div>
      )}
      {job.outcome && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="status-outcome">
          <div className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700"><Truck size={12} /> Dispatch</div>
          <p><b>Outcome:</b> {titleCase(job.outcome.result)} · <b>Gatepass:</b> {job.outcome.gatepass}</p>
          <p><b>Dispatched:</b> {fmt(job.outcome.dispatch_date)}{job.outcome.courier && <> · <b>Courier:</b> {job.outcome.courier}</>}</p>
          {job.outcome.warranty_until && <p><b>Warranty until:</b> {new Date(job.outcome.warranty_until).toLocaleDateString()}</p>}
        </div>
      )}
    </>
  );
}

function BatchView({ batch }) {
  return (
    <>
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight" data-testid="status-heading">Batch {batch.batch_number}</h1>
      <p className="mb-5 text-sm text-slate-500">{batch.quantity} × {batch.equipment_name}</p>
      <Info rows={[["Equipment", batch.equipment_name], ["Model", batch.model_number], ["Brand", batch.brand], ["Units", batch.quantity], ["Received", fmt(batch.received_at)]]} />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="status-counts">
        {batch.counts.map((c) => (
          <span key={c.status} className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{c.label}: {c.count}</span>
        ))}
      </div>
      <div className="max-h-96 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Serial</th><th className="px-3 py-2">Job</th><th className="px-3 py-2">Status</th></tr></thead>
          <tbody>{batch.units.map((u) => (
            <tr key={u.job_number} className="border-t border-slate-100"><td className="px-3 py-1.5 font-mono text-xs">{u.serial_number}</td><td className="px-3 py-1.5 font-mono text-xs">{u.job_number}</td><td className="px-3 py-1.5 font-semibold">{u.status_label}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </>
  );
}

export default function StatusPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/public/status/${encodeURIComponent(token)}`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e.response?.data?.detail)));
  }, [token]);
  return (
    <Shell>
      {err ? <p className="text-sm text-red-600" data-testid="status-error"><Package size={16} className="mr-1 inline" />{err}</p>
        : !data ? <p className="text-sm text-slate-500">Loading…</p>
        : data.kind === "job" ? <JobView job={data.job} /> : <BatchView batch={data.batch} />}
    </Shell>
  );
}
